import { reskinStyles } from '../theme-variant';

const overrideInput = (theme) => {
  theme.overrides.MuiInput = {
    root: {
      padding: 0,
      color: theme.colorPalette.beige_50_op_70,
      border: 'none',
      ' label + &': {
        marginTop: theme.spacing(3)
      },
      '& input:focus': {
        borderRadius: 0,
        border: 'none',
        boxShadow: 'none',
        padding: '1px 7px'
      },
      // Reskin: quote's field. The wrapper is the white pill, so adornments sit inside the border.
      ...reskinStyles(theme, {
        backgroundColor: theme.colorPalette.white,
        border: `1px solid ${theme.colors.form_border_primary}`,
        borderRadius: 23,
        padding: '0 16px',
        color: theme.colorPalette.green_100,
        '& input:focus': {
          border: 'none',
          boxShadow: 'none',
          padding: 0
        },
        '&$focused': {
          borderColor: theme.colorPalette.orange_50
        },
        // The full shorthand: the global `.Mui-error` rule from MuiInputLabel's override clears borders.
        '&$error': {
          border: `1px solid ${theme.colors.error}`
        }
      })
    },
    input: {
      padding: '0 7px',
      backgroundColor: theme.colorPalette.white_op_15,
      color: theme.colorPalette.white,
      fontSize: 14,
      height: 36,
      width: '100%',
      boxSizing: 'border-box',
      '&$disabled': {
        color: 'unset'
      },
      ...reskinStyles(theme, { backgroundColor: 'transparent', color: theme.colorPalette.green_100, padding: 0 })
    },
    inputMultiline: {
      border: '1px solid transparent',
      padding: '7px',
      // TODO: minHeight should be removed from MuiFormControl root override to generalize spacing between elements
      marginBottom: 12,
      backgroundColor: theme.colorPalette.white_op_15,
      color: theme.colorPalette.white,
      fontSize: 14,
      width: '100%',
      height: 'auto !important',
      '&$disabled': {
        color: 'unset'
      },
      ...reskinStyles(theme, { backgroundColor: 'transparent', color: theme.colorPalette.green_100, padding: '7px 0' })
    },
    error: {
      '& input:focus': {
        border: `1px solid ${theme.palette.error.main}`,
        boxShadow: `0 0 0 1px ${theme.palette.error.main}`,
        padding: '1px 7px'
      },
      '& textarea:focus': {
        border: `1px solid ${theme.palette.error.main}`,
        boxShadow: `0 0 0 1px ${theme.palette.error.main}`,
        padding: '7px',
        color: theme.palette.common.black
      },
      // Reskin: the pill's own border shows the error, so the input inside it stays plain.
      ...reskinStyles(theme, {
        '& input:focus': { border: 'none', boxShadow: 'none', padding: 0 },
        '& textarea:focus': { border: 'none', boxShadow: 'none', padding: '7px 0', color: theme.colorPalette.green_100 }
      })
    },
    disabled: {
      opacity: 0.4,
      color: 'unset'
    }
  };

  theme.overrides.MuiInputLabel = {
    root: {
      paddingLeft: 5,
      color: theme.colorPalette.beige_50_op_70,
      fontSize: 14,
      transform: 'translate(0, 27px) scale(1)',
      ...reskinStyles(theme, {
        // In line with the pill's 16px text inset until the label shrinks above the field.
        paddingLeft: 17,
        // Above the input's white pill, which comes later in the DOM and would cover it.
        zIndex: 1,
        color: theme.colors.text_secondary,
        '&$focused': {
          color: theme.colorPalette.orange_50
        },
        // After focused, so a field in error keeps a red label while typing.
        '&$error': {
          color: theme.colors.error
        }
      })
    },
    formControl: {
      transform: 'translate(0, 27px) scale(1)'
    },
    shrink: {
      textTransform: 'uppercase',
      transform: 'translate(-4px, 1.5px) scale(0.68)',
      // Reskin: the shrunk label renders at 12px (14px × 0.857), the new look's minimum for uppercase labels.
      // Field labels are sentence case in the new look (core/helpers/sentence-case.js), not uppercase.
      ...reskinStyles(theme, {
        paddingLeft: 5,
        textTransform: 'none',
        transform: 'translate(-4px, 1.5px) scale(0.857)'
      })
    },
    disabled: {
      color: theme.colorPalette.beige_50_op_70,
      opacity: 0.4,
      ...reskinStyles(theme, { color: theme.colors.text_secondary, opacity: 0.7 })
    },
    error: {
      border: 'none',
      color: theme.colors.error
    }
  };

  theme.overrides.MuiSelect = {
    selectMenu: {
      lineHeight: 36
    }
  };

  theme.overrides.MuiFormControl = {
    root: {
      // @TODO: minHeight here makes it difficult to generalize spacing between elements. This should be rethought.
      minHeight: 66,
      marginBottom: 12,
      // TODO: Remove "no-margin-bottom" class once we migrate to use new forms in the whole application
      '&.no-margin-bottom': {
        marginBottom: 0
      },
      '&.no-min-height': {
        minHeight: 'unset'
      },
      '&.has-custom-error': {
        minHeight: 40
      }
    }
  };

  theme.overrides.MuiFormLabel = {
    root: {
      '&$error': {
        color: theme.colors.error
      }
    }
  };

  theme.overrides.MuiFormHelperText = {
    root: {
      '&$error': {
        color: theme.colors.error
      }
    }
  };

  theme.overrides.MuiInputAdornment = {
    positionEnd: {
      position: 'absolute',
      color: theme.colorPalette.beige_50_op_70,
      right: 8,
      ...reskinStyles(theme, { color: theme.colors.text_secondary, right: 16 })
    }
  };

  theme.overrides.Mui = {
    disabled: {
      color: 'unset'
    }
  };
};

export default overrideInput;
